import type { ReactNode } from 'react';
import MenuItem from '@mui/material/MenuItem';
import type { AppSnapshot } from '../../app/state';
import { callAction } from '../../app/actions';
import { canBackupAdmin, canEditLinks, canEditMap, canEnterEdit, canUserPerms } from '../../lib/permissions';
import { canAiAdminHere } from '../../lib/capabilities';
import { anyExpanded as anyExpandedIn } from '../../lib/graph';
import { LanguagePicker } from '../../components/LanguagePicker';

/**
 * THE TOP BAR'S CONTROL REGISTRY — one definition per control that is allowed to leave the
 * bar for a "…" overflow menu (plan 1.3).
 *
 * Each control exists in exactly ONE of two places at any moment: inline in its zone, or as a
 * MenuItem inside that zone's overflow menu — never both. `inline()` renders character-for-
 * character the markup the zones used to carry themselves (same classes, same ids, same
 * attribute set), because the pixel baselines are the record of what the bar looks like and
 * the registry extraction was verified against them as a pure refactor before any overflow
 * behaviour existed. `menuItems()` renders the same control as one or more MUI MenuItems,
 * each with an `id="ovf-btn-*"` mirroring its inline id.
 *
 * WHAT IS DELIBERATELY NOT HERE: the brand, the edit/view switch, the search zone, the
 * presence stack, the user chip and the logout button. Those never overflow — the presence
 * stack self-compacts to "+N" and the user chip is CSS-hidden below 720px — so modelling them
 * would only invite pulling something that must never be pulled.
 *
 * PRIORITIES are per side, FIRST-OUT order (a user decision recorded in the plan):
 * right: Show links → Expand all → Theme → Help → Permissions → Language;
 * left:  Dashboard → Realign → AI doc → Undo → Link.
 * The cross-side rule lives in `topbarFit.ts`: the right side gives first, and the left —
 * the tools of the mode the user is actively working in — only when the right is exhausted.
 *
 * The language <select> is the one control that changes shape in the menu: it becomes a
 * "Language" group of radio-style MenuItems (checkmark on the current locale), invoking the
 * same `setLocale` the select does.
 */

export interface TopbarControl {
  /** Registry id — what `S.topbarOverflow` holds. */
  id: string;
  /** The inline element's DOM id, which is what the fit controller measures. */
  domId: string;
  side: 'left' | 'right';
  /** The permission/mode gate. An invisible control is neither inline nor in the menu. */
  visible: (s: AppSnapshot) => boolean;
  /** The inline rendering — exactly the markup the zone rendered before the registry. */
  inline: (s: AppSnapshot) => ReactNode;
  /** The same control as menu rows. `close` dismisses the menu before the action runs. */
  menuItems: (s: AppSnapshot, close: () => void) => ReactNode;
}

/**
 * "Is any branch open" — decides whether the toggle offers Collapse or Expand.
 *
 * The predicate itself lives in `lib/graph.ts`, where `actions.ts` reads it too: the label and
 * the action are one rule now, and V0.48.1 reversed that rule in one place. Computed per call
 * rather than memoised: the snapshot's containers are new on every publish, so a memo keyed on
 * them would recompute every time regardless.
 */
export const anyExpanded = (s: AppSnapshot): boolean => anyExpandedIn(s.nodes, s.edges);

/* --------------------------------------------------------------- the right-side controls */

const expandToggle: TopbarControl = {
  id: 'expandtoggle',
  domId: 'btn-expandtoggle',
  side: 'right',
  visible: () => true,
  inline: (s) => {
    const open = anyExpanded(s);
    return (
      <button
        className="btn"
        id="btn-expandtoggle"
        title={open ? 'Collapse all branches' : 'Expand all branches'}
        onClick={() => callAction('toggleExpandAll')}
      >
        <svg className="ico">
          <use id="expandtoggle-use" href={open ? '#i-collapseall' : '#i-expandall'} />
        </svg>
        <span id="expandtoggle-label">{open ? 'Collapse all' : 'Expand all'}</span>
      </button>
    );
  },
  menuItems: (s, close) => {
    const open = anyExpanded(s);
    return (
      <MenuItem
        key="expandtoggle"
        id="ovf-btn-expandtoggle"
        onClick={() => {
          close();
          callAction('toggleExpandAll');
        }}
      >
        <svg className="ico">
          <use href={open ? '#i-collapseall' : '#i-expandall'} />
        </svg>
        <span>{open ? 'Collapse all' : 'Expand all'}</span>
      </MenuItem>
    );
  },
};

const linksToggle: TopbarControl = {
  id: 'links',
  domId: 'btn-links',
  side: 'right',
  // Only meaningful when the map has cross-links to hide.
  visible: (s) => s.links.length > 0,
  inline: (s) => (
    <button
      className={s.showLinks ? 'btn on' : 'btn'}
      id="btn-links"
      title={
        s.showLinks
          ? 'Dotted links are shown — click to hide them'
          : 'Dotted links are hidden — click to show them'
      }
      onClick={() => callAction('toggleLinks')}
    >
      <svg className="ico">
        <use href="#i-links" />
      </svg>
      <span id="links-label">{s.showLinks ? 'Hide links' : 'Show links'}</span>
    </button>
  ),
  menuItems: (s, close) => (
    <MenuItem
      key="links"
      id="ovf-btn-links"
      selected={s.showLinks}
      title={
        s.showLinks
          ? 'Dotted links are shown — click to hide them'
          : 'Dotted links are hidden — click to show them'
      }
      onClick={() => {
        close();
        callAction('toggleLinks');
      }}
    >
      <svg className="ico">
        <use href="#i-links" />
      </svg>
      <span>{s.showLinks ? 'Hide links' : 'Show links'}</span>
    </MenuItem>
  ),
};

const langPicker: TopbarControl = {
  id: 'lang',
  domId: 'lang-select',
  side: 'right',
  visible: (s) => s.languages.length > 1,
  /* The shared `LanguagePicker` (V0.53.5) — the one implementation every surface renders,
     controlled from the snapshot. `inline` ignores its `s`: the component reads the same
     snapshot through the selector hook. */
  inline: () => <LanguagePicker id="lang-select" title="Language" />,
  // Inside the menu the <select> becomes a radio-style group: a disabled "Language" header,
  // then one row per locale with a checkmark on the current one, firing the same setLocale.
  menuItems: (s, close) => [
    <MenuItem key="lang-head" disabled>
      Language
    </MenuItem>,
    ...s.languages.map((l) => (
      <MenuItem
        key={`lang-${l.code}`}
        id={`ovf-btn-lang-${l.code}`}
        selected={l.code === s.currentLocale}
        onClick={() => {
          close();
          void callAction('setLocale', l.code);
        }}
      >
        <svg
          className="ico"
          style={{ width: 14, height: 14, visibility: l.code === s.currentLocale ? 'visible' : 'hidden' }}
        >
          <use href="#i-check" />
        </svg>
        <span>{l.name}</span>
      </MenuItem>
    )),
  ],
};

const helpButton: TopbarControl = {
  id: 'help',
  domId: 'btn-help',
  side: 'right',
  visible: () => true,
  inline: () => (
    <button className="btn" id="btn-help" title="Help & guide" onClick={() => callAction('openHelp')}>
      <svg className="ico">
        <use href="#i-info" />
      </svg>
    </button>
  ),
  menuItems: (_s, close) => (
    <MenuItem
      key="help"
      id="ovf-btn-help"
      onClick={() => {
        close();
        callAction('openHelp');
      }}
    >
      <svg className="ico">
        <use href="#i-info" />
      </svg>
      <span>Help & guide</span>
    </MenuItem>
  ),
};

const themeButton: TopbarControl = {
  id: 'theme',
  domId: 'btn-theme',
  side: 'right',
  visible: () => true,
  /* The icon is deliberately fixed. It reads as "there is a light mode", not as a report
     of the current one, and the vanilla button never swapped it either. */
  inline: () => (
    <button className="btn" id="btn-theme" onClick={() => callAction('toggleTheme')}>
      <svg className="ico">
        <use href="#i-sun" />
      </svg>
    </button>
  ),
  menuItems: (_s, close) => (
    <MenuItem
      key="theme"
      id="ovf-btn-theme"
      onClick={() => {
        close();
        callAction('toggleTheme');
      }}
    >
      <svg className="ico">
        <use href="#i-sun" />
      </svg>
      <span>Switch theme</span>
    </MenuItem>
  ),
};

const permsButton: TopbarControl = {
  id: 'perms',
  domId: 'btn-perms',
  side: 'right',
  // EITHER permission (V0.48). The button, its icon and its label are unchanged — a backup
  // administrator reaches the same page and finds only the Backups tab on it. Giving the
  // standalone role a second topbar button was the alternative, and it was rejected: six of
  // the nine pixel baselines photograph this bar, and one more control in it is a UI change
  // nobody asked for.
  visible: (s) => canUserPerms(s.perm) || canBackupAdmin(s.perm),
  inline: () => (
    <button
      className="btn"
      id="btn-perms"
      title="Users, groups & permissions"
      onClick={() => callAction('openPermPage')}
    >
      <svg className="ico">
        <use href="#i-users" />
      </svg>
      <span>Permissions</span>
    </button>
  ),
  menuItems: (_s, close) => (
    <MenuItem
      key="perms"
      id="ovf-btn-perms"
      title="Users, groups & permissions"
      onClick={() => {
        close();
        callAction('openPermPage');
      }}
    >
      <svg className="ico">
        <use href="#i-users" />
      </svg>
      <span>Permissions</span>
    </MenuItem>
  ),
};

/* ---------------------------------------------------------------- the left-side controls */

const linkTool: TopbarControl = {
  id: 'link',
  domId: 'btn-link',
  side: 'left',
  // Cross-links are their own brick (V0.37) — a participant without it gets no dead tool.
  visible: (s) => s.editing && canEditLinks(s.perm),
  inline: (s) => (
    <button className={s.linkArmed ? 'btn on' : 'btn'} id="btn-link" onClick={() => callAction('armLink')}>
      <svg className="ico">
        <use href="#i-link" />
      </svg>
      <span>Link</span>
    </button>
  ),
  menuItems: (s, close) => (
    <MenuItem
      key="link"
      id="ovf-btn-link"
      selected={s.linkArmed}
      onClick={() => {
        close();
        callAction('armLink');
      }}
    >
      <svg className="ico">
        <use href="#i-link" />
      </svg>
      <span>Link</span>
    </MenuItem>
  ),
};

const realignButton: TopbarControl = {
  id: 'realign',
  domId: 'btn-realign',
  side: 'left',
  // Realign publishes derived positions to everybody — a nodes-family write, so EDIT_MAP.
  visible: (s) => s.editing && canEditMap(s.perm),
  inline: () => (
    <button className="btn" id="btn-realign" onClick={() => callAction('realign')}>
      <svg className="ico">
        <use href="#i-align" />
      </svg>
      <span>Realign</span>
    </button>
  ),
  menuItems: (_s, close) => (
    <MenuItem
      key="realign"
      id="ovf-btn-realign"
      onClick={() => {
        close();
        callAction('realign');
      }}
    >
      <svg className="ico">
        <use href="#i-align" />
      </svg>
      <span>Realign</span>
    </MenuItem>
  ),
};

const undoButton: TopbarControl = {
  id: 'undo',
  domId: 'btn-undo',
  side: 'left',
  visible: (s) => s.editing,
  /* The title names what would be undone, which is the only way to tell before
     clicking. Disabled and "Nothing to undo" are one state, not two. */
  inline: (s) => (
    <button
      className="btn"
      id="btn-undo"
      disabled={!s.undoLabel}
      title={s.undoLabel ? `Undo: ${s.undoLabel}` : 'Nothing to undo'}
      onClick={() => callAction('performUndo')}
    >
      <svg className="ico">
        <use href="#i-undo" />
      </svg>
      <span>Undo</span>
    </button>
  ),
  menuItems: (s, close) => (
    <MenuItem
      key="undo"
      id="ovf-btn-undo"
      disabled={!s.undoLabel}
      title={s.undoLabel ? `Undo: ${s.undoLabel}` : 'Nothing to undo'}
      onClick={() => {
        close();
        callAction('performUndo');
      }}
    >
      <svg className="ico">
        <use href="#i-undo" />
      </svg>
      <span>Undo</span>
    </MenuItem>
  ),
};

const dashboardButton: TopbarControl = {
  id: 'dashboard',
  domId: 'btn-dashboard',
  side: 'left',
  visible: (s) => s.editing,
  inline: () => (
    <button
      className="btn"
      id="btn-dashboard"
      title="Documentation coverage dashboard"
      onClick={() => callAction('openDashboard')}
    >
      <svg className="ico">
        <use href="#i-chart" />
      </svg>
      <span>Dashboard</span>
    </button>
  ),
  menuItems: (_s, close) => (
    <MenuItem
      key="dashboard"
      id="ovf-btn-dashboard"
      title="Documentation coverage dashboard"
      onClick={() => {
        close();
        callAction('openDashboard');
      }}
    >
      <svg className="ico">
        <use href="#i-chart" />
      </svg>
      <span>Dashboard</span>
    </MenuItem>
  ),
};

const heatmapButton: TopbarControl = {
  id: 'heatmap',
  domId: 'btn-heatmap',
  side: 'left',
  // Any participant (V0.39): canEnterEdit is the client mirror of the health endpoint's own
  // gate, so the toggle exists for exactly the population the fetch would answer. Edit-mode
  // only, which is what keeps every view-mode pixel baseline free of it.
  visible: (s) => s.editing && canEnterEdit(s.perm),
  inline: (s) => (
    <button
      className={s.heatmapOn ? 'btn on' : 'btn'}
      id="btn-heatmap"
      title="Clearance heatmap — colour nodes by how their documentation stands"
      onClick={() => callAction('toggleHeatmap')}
    >
      <svg className="ico">
        <use href="#i-heat" />
      </svg>
      <span>Heatmap</span>
    </button>
  ),
  menuItems: (s, close) => (
    <MenuItem
      key="heatmap"
      id="ovf-btn-heatmap"
      selected={s.heatmapOn}
      title="Clearance heatmap — colour nodes by how their documentation stands"
      onClick={() => {
        close();
        callAction('toggleHeatmap');
      }}
    >
      <svg className="ico">
        <use href="#i-heat" />
      </svg>
      <span>Heatmap</span>
    </MenuItem>
  ),
};

const aiDocButton: TopbarControl = {
  id: 'aidoc',
  domId: 'btn-aidoc',
  side: 'left',
  // Role AND deployment capability, independently — the air-gapped contract.
  visible: (s) => s.editing && canAiAdminHere(s.perm, s.caps),
  inline: (s) => (
    <button
      className={s.aiDocMode ? 'btn on' : 'btn'}
      id="btn-aidoc"
      title="AI documentation tools"
      onClick={() => callAction('toggleAiDocMode')}
    >
      <svg className="ico">
        <use href="#i-ai" />
      </svg>
      <span>AI doc</span>
    </button>
  ),
  menuItems: (s, close) => (
    <MenuItem
      key="aidoc"
      id="ovf-btn-aidoc"
      selected={s.aiDocMode}
      title="AI documentation tools"
      onClick={() => {
        close();
        callAction('toggleAiDocMode');
      }}
    >
      <svg className="ico">
        <use href="#i-ai" />
      </svg>
      <span>AI doc</span>
    </MenuItem>
  ),
};

/* --------------------------------------------------------------------------- the rosters */

/** Right zone, in DOM ORDER — the order the bar has always rendered them in. */
export const RIGHT_CONTROLS: readonly TopbarControl[] = [
  expandToggle,
  linksToggle,
  langPicker,
  helpButton,
  themeButton,
  permsButton,
];

/** Left zone, in DOM ORDER. */
export const LEFT_CONTROLS: readonly TopbarControl[] = [
  linkTool,
  realignButton,
  undoButton,
  dashboardButton,
  heatmapButton,
  aiDocButton,
];

/** FIRST-OUT priority per side (user decision — see the header). The heatmap toggle leaves
 *  right after the dashboard: both are read-the-map surfaces, not editing tools. */
export const RIGHT_PRIORITY: readonly string[] = ['links', 'expandtoggle', 'theme', 'help', 'perms', 'lang'];
export const LEFT_PRIORITY: readonly string[] = ['dashboard', 'heatmap', 'realign', 'aidoc', 'undo', 'link'];
